/**
 * Page Layout Component
 * Shared layout for all authenticated pages
 * Contains: Welcome Banner, Navigation Tabs, Main Content, Sidebar
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSupport } from '../context/SupportContext';
import { SupportCasesPanel, AccountTeamPanel } from './dashboard';
import styles from './PageLayout.module.css';

interface TeamMember {
  name: string;
  role: string;
  email: string;
  phone: string;
  avatar?: string;
}

interface SupportCasesData {
  needsAttention: number;
  inProgress: number;
  notStarted: number;
}

interface PageLayoutProps {
  children: React.ReactNode;
  supportCases?: SupportCasesData;
  accountTeam?: TeamMember[];
  showSidebar?: boolean;
  showWelcome?: boolean;
  userProfile?: {
    firstName?: string;
    lastName?: string;
    fullName?: string;
    email?: string;
  };
}

const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  supportCases,
  accountTeam,
  showSidebar = true,
  showWelcome = true,
  userProfile,
}) => {
  const { user } = useAuth();
  const location = useLocation();

  // Navigation tabs (specific to dashboard/home navigation)
  const navTabs = [
    { label: 'Overview', path: '/' },
    { label: 'Learning', path: '/learning' },
    { label: 'Knowledge Articles', path: '/knowledge' },
    { label: 'Technical Documentation', path: '/documentation' },
    { label: 'Community', path: '/community' },
    { label: 'Support', path: '/support' },
  ];

  const isActiveTab = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const { metrics: globalMetrics, accountTeam: globalAccountTeam } = useSupport();
  const displaySupportCases = supportCases || globalMetrics;
  const displayTeamMembers = accountTeam || globalAccountTeam;

  return (
    <div className={styles.pageLayout}>
      {/* Welcome Banner - Removed header from here as it's now in global Layout.tsx */}
      {showWelcome && (
        <div className={styles.welcomeBanner}>
          <div className={styles.welcomeContent}>
            <div className={styles.welcomeText}>
              <div className={styles.welcomeLabel}>WELCOME</div>
              <div className={styles.welcomeName}>
                {userProfile?.fullName || (userProfile?.firstName && userProfile?.lastName ? `${userProfile.firstName} ${userProfile.lastName}` : null) || (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'John Smith')}
              </div>
              <div className={styles.welcomeSubtext}>
                Get started with Commvault Customer Support Center
              </div>
            </div>
            <button className={styles.launchButton}>Launch Commvault Cloud</button>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <nav className={styles.navTabs}>
        <div className={styles.navTabsContent}>
          {navTabs.map((tab) => (
            <Link
              key={tab.path}
              to={tab.path}
              className={`${styles.navTab} ${isActiveTab(tab.path) ? styles.navTabActive : ''}`}
            >
              {tab.label}
              {tab.label === 'Support' && (
                <svg className={styles.dropdownIcon} width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </Link>
          ))}
        </div>
      </nav>

      {/* Main Grid */}
      <div className={`${styles.mainGrid} ${!showSidebar ? styles.mainGridNoSidebar : ''}`}>
        <div className={styles.mainContent}>
          {children}
        </div>

        {showSidebar && (
          <aside className={styles.sidebar}>
            <SupportCasesPanel
              needsAttention={displaySupportCases.needsAttention}
              inProgress={displaySupportCases.inProgress}
              notStarted={displaySupportCases.notStarted}
            />
            <AccountTeamPanel teamMembers={displayTeamMembers} />
          </aside>
        )}
      </div>
    </div>
  );
};

export default PageLayout;
